import React, { useState } from 'react';
import { AppstoreOutlined, ArrowRightOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
import './HomePage.css';

export type HomeMenuItem = { key?: string; type?: string; label?: React.ReactNode; icon?: React.ReactNode; children?: HomeMenuItem[] };

const PREVIEW_COUNT = 6;
const groupPresentation: Record<string, { tone: string; description: string }> = {
  'supply-chain': { tone: 'blue', description: '从采购计划到交付，协同每一个环节' },
  ecommerce: { tone: 'orange', description: '连接渠道、内容与日常经营' },
  'creative-marketing': { tone: 'violet', description: '让创意与内容，在这里开始' },
  finance: { tone: 'green', description: '清晰对账，有序管理财务工作' },
  engineering: { tone: 'slate', description: '工程资料整理与生成' },
  'product-engineering': { tone: 'blue', description: '连接产品开发、研发协同与工程资料' },
  quality: { tone: 'green', description: '把好来料、出货与产品配置关' },
  'foreign-trade': { tone: 'violet', description: '客户、商机与沟通，一站直达' },
  knowledge: { tone: 'orange', description: '查找资料，分享经验，积累知识' },
  investment: { tone: 'blue', description: '商家、门店与合作业务协同' },
  executive: { tone: 'slate', description: '从经营总览到业务明细' },
  wuyou: { tone: 'green', description: '订单办理、派单与售后服务' },
  system: { tone: 'slate', description: '配置工作规则与基础能力' },
};

/** 仅搜索已经授权的菜单，不维护第二套路由或权限目录。 */
function labelText(label: React.ReactNode): string {
  if (typeof label === 'string' || typeof label === 'number') return String(label);
  if (Array.isArray(label)) return label.map(labelText).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(label)) return labelText(label.props.children);
  return '';
}

function entryLink(item: HomeMenuItem, className: string) {
  const content = <>
    <span className="work-home-entry-icon" aria-hidden="true">{item.icon || <AppstoreOutlined />}</span>
    <span className="work-home-entry-label">{React.isValidElement<{ children?: React.ReactNode }>(item.label) ? item.label.props.children : item.label}</span>
    <ArrowRightOutlined className="work-home-entry-arrow" aria-hidden="true" />
  </>;
  // 保留原 Link 的路由及行为，让整个入口可点击。
  return React.isValidElement<{ className?: string; children?: React.ReactNode }>(item.label)
    ? React.cloneElement(item.label, { className }, content)
    : <span className={className}>{content}</span>;
}

/** 复用已经过滤的导航，不请求业务数据，不预加载业务模块。 */
export default function HomePage({ items, loaded }: { items: HomeMenuItem[]; loaded: boolean }) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const query = search.trim().toLocaleLowerCase();
  const entries = loaded ? items.filter(item => item.type !== 'divider' && item.key !== '/home') : [];
  const groups = entries.filter(item => item.children?.length).map(item => ({
    ...item,
    children: !query || labelText(item.label).toLocaleLowerCase().includes(query) ? item.children!
      : item.children!.filter(child => labelText(child.label).toLocaleLowerCase().includes(query)),
  })).filter(item => item.children.length);
  const tools = entries.filter(item => !item.children && (!query || labelText(item.label).toLocaleLowerCase().includes(query)));
  const resultCount = groups.reduce((count, group) => count + group.children.length, tools.length);
  return <main className="work-home">
    <header className="work-home-header">
      <div className="work-home-heading">
        <span className="work-home-brand-icon" aria-hidden="true"><AppstoreOutlined /></span>
        <div><span className="work-home-eyebrow">天能 AI · 工作空间</span>
          <h1>工作首页</h1><p>找到所需工具，开始今天的工作。</p>
        </div>
      </div>
      <div className="work-home-search">
        <SearchOutlined aria-hidden="true" />
        <input type="search" aria-label="搜索工作入口" placeholder="搜索应用或功能，如采购、客户…"
          value={search} onChange={event => setSearch(event.target.value)} disabled={!loaded} />
        {search && <button type="button" aria-label="清空搜索" onClick={() => setSearch('')}><CloseOutlined /></button>}
      </div>
    </header>
    {!loaded ? <div role="status" className="work-home-loading">正在读取你的工作入口…</div> :
      <>
        <div className="work-home-section-heading"><h2>{query ? '搜索结果' : '业务应用'}</h2>
          <span role="status" aria-live="polite">{query ? `找到 ${resultCount} 个入口` : '按业务分类，快速进入'}</span>
        </div>
        <div className="work-home-grid">{groups.map(item => {
          const key = item.key || labelText(item.label);
          const presentation = groupPresentation[key] || { tone: 'blue', description: '你的专属工作入口' };
          const isExpanded = !!query || !!expanded[key];
          const visible = isExpanded ? item.children : item.children.slice(0, PREVIEW_COUNT);
          return <section className={`work-home-card work-home-tone-${presentation.tone}`} key={key}>
            <div className="work-home-card-heading">
              <span className="work-home-category-icon" aria-hidden="true">{item.icon || <AppstoreOutlined />}</span>
              <div><h3>{item.label}</h3><p>{presentation.description}</p></div>
            </div>
            <nav className="work-home-card-links" aria-label={labelText(item.label)} id={`home-group-${key}`}>
              {visible.map(child => <React.Fragment key={child.key}>{entryLink(child, 'work-home-entry')}</React.Fragment>)}
            </nav>
            <div className="work-home-card-footer"><span>{item.children.length} 个功能</span>
              {!query && item.children.length > PREVIEW_COUNT
                ? <button type="button" aria-expanded={!!expanded[key]} aria-controls={`home-group-${key}`}
                  aria-label={`${expanded[key] ? '收起' : '展开'}${labelText(item.label)}功能`}
                  onClick={() => setExpanded(previous => ({ ...previous, [key]: !previous[key] }))}>
                  {expanded[key] ? '收起' : `更多 ${item.children.length - PREVIEW_COUNT} 个`}<span aria-hidden="true">{expanded[key] ? '−' : '+'}</span>
                </button> : <span className="work-home-card-caption">点击功能进入 <ArrowRightOutlined aria-hidden="true" /></span>}
            </div>
          </section>;
        })}</div>
        {!!tools.length && <section className="work-home-tools" aria-label="工具与服务">
          <div className="work-home-section-heading"><h2>工具与服务</h2><span>更多工作支持</span></div>
          <nav className="work-home-tools-grid" aria-label="工具与服务入口">{tools.map(item =>
            <React.Fragment key={item.key}>{entryLink(item, 'work-home-tool')}</React.Fragment>)}</nav>
        </section>}
        {resultCount === 0 && <div className="work-home-empty">
          <SearchOutlined aria-hidden="true" /><h3>{query ? '没有找到相关入口' : '暂无可用的工作入口'}</h3>
          <p>{query ? '换个关键词试试，或清空搜索查看全部应用。' : '工作入口会根据你的权限展示。'}</p>
          {query && <button type="button" onClick={() => setSearch('')}>查看全部应用</button>}
        </div>}
      </>}
    <footer className="work-home-footer"><span>天能 AI</span><span>让工作更有序，让协作更简单。</span></footer>
  </main>;
}
